// src/sign_in_files/detailsScreen1.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StepIndicator, PrimaryButton, SecondaryButton, InputField, SelectField, TextareaField,
  ErrorMessage, LoadingOverlay, SuccessModal, AutoSaveIndicator, FormCard, LeftSection,
  useAutoSave, FIELD_LIMITS, SuccessModal as SuccessModalComponent
} from './DesignSystem';
// Fix the import - remove the incorrect import from DesignSystem
import fundraiserApi from '../services/fundraiserApi';

// Import the success component
import FundraiserSubmissionSuccess from '../pages/FundraiserSubmissionSuccess';

const DetailsScreen_1 = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1
    title: '',
    goalAmount: '',
    purpose: 'medical',
    patientRelation: '',
    education: '',
    employmentStatus: '',
    heardAbout: '', // Added missing field
    
    // Step 2
    patientName: '',
    patientAge: '',
    medicalCondition: '',
    hospitalizationStatus: '',
    hospital: '',
    city: '',
    
    // Step 3
    story: '',
    addDocuments: false,
    consentInsurance: true,
    agreeTerms: false, // Default to false, user must check
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [fundraiserId, setFundraiserId] = useState(null);
  const [generatedStory, setGeneratedStory] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false); // New state for success screen
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showMedicalConditionDropdown, setShowMedicalConditionDropdown] = useState(false);
  const [filteredConditions, setFilteredConditions] = useState([]);
  
  // Add OTP state variables
  const [otp, setOtp] = useState('');
  const [otpTxnId, setOtpTxnId] = useState('');

  // Auto-save setup
  const { lastSaved, isSaving, saveError, loadDraft, clearDraft } = useAutoSave(
    formData,
    'fundraiser_details'
  );

  // Medical conditions list
  const medicalConditions = [
    'Cancer',
    'Heart Disease',
    'Stroke',
    'Diabetes',
    'Kidney Disease',
    'Liver Disease',
    'COVID-19',
    'Pneumonia',
    'Tuberculosis',
    'Asthma',
    'Chronic Obstructive Pulmonary Disease (COPD)',
    'Alzheimer\'s Disease',
    'Parkinson\'s Disease',
    'Multiple Sclerosis',
    'Epilepsy',
    'Brain Tumor',
    'Spinal Cord Injury',
    'Arthritis',
    'Osteoporosis',
    'Lupus',
    'HIV/AIDS',
    'Hepatitis',
    'Leukemia',
    'Lymphoma',
    'Breast Cancer',
    'Lung Cancer',
    'Prostate Cancer',
    'Colon Cancer',
    'Pancreatic Cancer',
    'Ovarian Cancer',
    'Brain Cancer',
    'Liver Cancer',
    'Kidney Cancer',
    'Bladder Cancer',
    'Skin Cancer (Melanoma)',
    'Thyroid Cancer',
    'Bone Cancer',
    'Blood Cancer',
    'Cystic Fibrosis',
    'Sickle Cell Anemia',
    'Thalassemia',
    'Hemophilia',
    'Muscular Dystrophy',
    'Cerebral Palsy',
    'Down Syndrome',
    'Autism Spectrum Disorder',
    'Schizophrenia',
    'Bipolar Disorder',
    'Major Depression',
    'Post-Traumatic Stress Disorder (PTSD)',
    'Eating Disorders',
    'Organ Transplant (Heart)',
    'Organ Transplant (Liver)',
    'Organ Transplant (Kidney)',
    'Organ Transplant (Lung)',
    'Organ Transplant (Bone Marrow)',
    'Accident Injuries',
    'Burn Injuries',
    'Spinal Injury',
    'Head Injury',
    'Amputation',
    'Rare Genetic Disorders',
    'Autoimmune Diseases',
    'Rheumatoid Arthritis',
    'Crohn\'s Disease',
    'Ulcerative Colitis',
    'Celiac Disease',
    'Sepsis',
    'Meningitis',
    'Encephalitis',
    'Guillain-Barré Syndrome',
    'Myasthenia Gravis',
    'Fibromyalgia',
    'Chronic Fatigue Syndrome',
    'End Stage Renal Disease (ESRD)',
    'Cirrhosis',
    'Heart Failure',
    'Cardiac Arrest',
    'Coronary Artery Disease',
    'Valvular Heart Disease',
    'Congenital Heart Disease',
    'Premature Birth Complications',
    'Neonatal Intensive Care (NICU)',
    'Pediatric Cancer',
    'Childhood Leukemia',
    'Other'
  ];

  const features = [
    { 
      title: "Zero Platform Fees", 
      description: "100% of donations go directly to your cause", 
      gradient: "from-green-500 to-emerald-600",
      icon: "💰"
    },
    { 
      title: "24/7 Support", 
      description: "Our team is always here to help you", 
      gradient: "from-blue-500 to-cyan-600",
      icon: "🛡️"
    },
    { 
      title: "Maximum Reach", 
      description: "Share with millions of potential donors", 
      gradient: "from-purple-500 to-indigo-600",
      icon: "🌍"
    }
  ];

  // Filter medical conditions based on input
  const filterMedicalConditions = (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setFilteredConditions([]);
      setShowMedicalConditionDropdown(false);
    } else {
      const filtered = medicalConditions.filter(condition =>
        condition.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredConditions(filtered);
      if (filtered.length > 0) {
        setShowMedicalConditionDropdown(true);
      } else {
        setShowMedicalConditionDropdown(false);
      }
    }
  };

  // Initialize - Check for existing draft
  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(featureInterval);
  }, []); // Empty dependency array - runs once on mount

  // Separate effect for loading draft data
  useEffect(() => {
    if (!isInitialLoad) return;
    
    const loadDraftData = async () => {
      // Check if we're coming from a refresh with a successful submission
      const submittedFundraiserId = localStorage.getItem('submittedFundraiserId');
      const submissionTime = localStorage.getItem('submissionTime');
      
      if (submittedFundraiserId && submissionTime) {
        const timeSinceSubmission = Date.now() - parseInt(submissionTime);
        // Show success screen if submission was within last 5 minutes
        if (timeSinceSubmission < 5 * 60 * 1000) {
          setFundraiserId(submittedFundraiserId);
          setShowSubmissionSuccess(true);
          setIsInitialLoad(false);
          return;
        } else {
          // Clear old submission data
          localStorage.removeItem('submittedFundraiserId');
          localStorage.removeItem('submissionTime');
        }
      }
      
      // Load OTP data from localStorage
      try {
        const userData = localStorage.getItem('completeUserData');
        if (userData) {
          const parsedData = JSON.parse(userData);
          if (parsedData.otp && parsedData.otpTxnId) {
            setOtp(parsedData.otp);
            setOtpTxnId(parsedData.otpTxnId);
            console.log('✅ Loaded OTP from localStorage');
          }
        }
      } catch (error) {
        console.error('Error loading OTP data:', error);
      }
      
      // Only load draft if we're not showing success screen
      if (!showSubmissionSuccess) {
        try {
          console.log('🔍 Checking for existing draft...');
          const draftData = await fundraiserApi.checkUserDraft();
          
          if (draftData && draftData.success && draftData.fundraiserId) {
            console.log('📄 Found existing draft:', draftData.fundraiserId);
            setFundraiserId(draftData.fundraiserId);
            localStorage.setItem('fundraiserId', draftData.fundraiserId);
            
            // Load detailed data
            const fundraiserDetails = await fundraiserApi.getFundraiserById(draftData.fundraiserId);
            
            if (fundraiserDetails && fundraiserDetails.success) {
              const data = fundraiserDetails.data;
              
              // Update form data with existing values
              setFormData(prev => ({
                ...prev,
                // Step 1 data
                title: data.title || '',
                goalAmount: data.goalAmount ? data.goalAmount.toString() : '',
                purpose: data.purpose || 'medical',
                patientRelation: data.relation || data.patientRelation || '',
                education: data.education || '',
                employmentStatus: data.employment || data.employmentStatus || '',
                heardAbout: data.ref_source || data.heardAbout || '',
                
                // Step 2 data (if exists)
                patientName: data.patientName || data.patient_name || data.name || '',
                patientAge: data.age ? data.age.toString() : data.patientAge || '',
                medicalCondition: data.condition || data.medicalCondition || '',
                hospitalizationStatus: data.hospitalStatus || data.hospitalizationStatus || '',
                hospital: data.hospital || '',
                city: data.city || '',
                
                // Step 3 data (if exists)
                story: data.story || data.storyHtml || '',
                addDocuments: data.add_documents || false,
                consentInsurance: data.consent_insurance !== undefined ? data.consent_insurance : true,
                agreeTerms: data.agree_terms || false,
              }));
              
              // Determine current step based on what's filled
              if (data.story && data.story.trim() !== '') {
                setActiveStep(3);
              } else if ((data.patientName || data.patient_name) && (data.patientName || data.patient_name).trim() !== '') {
                setActiveStep(2);
              } else if (data.title && data.title.trim() !== '') {
                setActiveStep(1);
              }
              
              setApiError({ type: 'info', message: 'Loaded existing draft fundraiser' });
              setTimeout(() => setApiError(''), 5000);
            }
          } else {
            // Load user data for new fundraiser
            try {
              const userData = localStorage.getItem('completeUserData');
              if (userData) {
                const parsedData = JSON.parse(userData);
                if (parsedData.name && !formData.title) {
                  setFormData(prev => ({
                    ...prev,
                    title: `Help ${parsedData.name} with medical treatment`
                  }));
                }
              }
            } catch (error) {
              console.error('Error loading user data:', error);
            }
            
            // Load draft from localStorage
            const savedDraft = loadDraft();
            if (savedDraft) {
              setFormData(savedDraft);
              setApiError({ type: 'info', message: 'Loaded previously saved draft' });
              setTimeout(() => setApiError(''), 5000);
            }
          }
        } catch (error) {
          console.error('Error loading draft:', error);
          
          // Fallback to localStorage draft
          try {
            const savedDraft = loadDraft();
            if (savedDraft) {
              setFormData(savedDraft);
            }
          } catch (localError) {
            console.error('Error loading local draft:', localError);
          }
        }
      }
      
      setIsInitialLoad(false);
    };

    loadDraftData();
  }, [loadDraft]); // Only depend on loadDraft

  // Initialize filtered conditions
  useEffect(() => {
    setFilteredConditions(medicalConditions);
  }, []);

  // Clear everything on page refresh/hard reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clear if we haven't submitted successfully
      if (!localStorage.getItem('submittedFundraiserId')) {
        localStorage.removeItem('fundraiserId');
        clearDraft();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [clearDraft]);

  const validateField = (field, value) => {
    let error = '';
    
    switch (field) {
      case 'title':
        if (!value.trim()) error = 'Title is required';
        else if (value.length < FIELD_LIMITS.title.min) error = `Title must be at least ${FIELD_LIMITS.title.min} characters`;
        break;
      case 'goalAmount':
        if (!value) error = 'Goal amount is required';
        else if (parseInt(value) < 2000) error = 'Minimum goal amount is ₹2000';
        else if (parseInt(value) > FIELD_LIMITS.goalAmount.max) error = `Maximum goal amount is ₹${FIELD_LIMITS.goalAmount.max.toLocaleString()}`;
        break;
      case 'patientName':
        if (!value.trim()) error = 'Patient name is required';
        else if (value.length < FIELD_LIMITS.patientName.min) error = `Name must be at least ${FIELD_LIMITS.patientName.min} characters`;
        break;
      case 'medicalCondition':
        if (!value.trim()) error = 'Medical condition is required';
        break;
      case 'hospital':
        if (!value.trim()) error = 'Hospital name is required';
        break;
      case 'story':
        if (!value.trim()) error = 'Story is required';
        else if (value.length < FIELD_LIMITS.story.min) error = `Story must be at least ${FIELD_LIMITS.story.min} characters`;
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleBlur = (field) => {
    const error = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
    if (field === 'medicalCondition') {
      setTimeout(() => setShowMedicalConditionDropdown(false), 200);
    }
  };

  const handleInputChange = (field, value) => {
    // Apply limits for numeric fields
    if (field === 'goalAmount') {
      const numValue = parseInt(value.replace(/\D/g, '') || '0');
      if (numValue > FIELD_LIMITS.goalAmount.max) return;
      value = numValue === 0 ? '' : numValue.toString();
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: '' }));
    if (apiError) setApiError('');

    // Handle medical condition search
    if (field === 'medicalCondition') {
      filterMedicalConditions(value);
    }
  };

  const handleSelectMedicalCondition = (condition) => {
    handleInputChange('medicalCondition', condition);
    setShowMedicalConditionDropdown(false);
  };

  const validateStep = () => {
    console.log('🔍 Validating step:', activeStep);
    console.log('📋 Form data for validation:', formData);
    
    const newErrors = {};
    const newFieldErrors = {};

    if (activeStep === 1) {
      const step1Fields = ['title', 'goalAmount', 'patientRelation', 'education', 'employmentStatus', 'heardAbout'];
      
      step1Fields.forEach(field => {
        console.log(`  Checking ${field}:`, formData[field]);
        
        if (!formData[field] || formData[field].toString().trim() === '') {
          newErrors[field] = true;
          newFieldErrors[field] = 'This field is required';
          console.log(`  ❌ ${field} is required`);
        } else if (field === 'goalAmount') {
          const amount = parseInt(formData[field]);
          if (isNaN(amount) || amount < 2000) {
            newErrors[field] = true;
            newFieldErrors[field] = 'Minimum goal amount is ₹2000';
            console.log(`  ❌ ${field} amount too low: ${amount}`);
          } else if (amount > FIELD_LIMITS.goalAmount.max) {
            newErrors[field] = true;
            newFieldErrors[field] = `Maximum goal amount is ₹${FIELD_LIMITS.goalAmount.max.toLocaleString()}`;
            console.log(`  ❌ ${field} amount too high: ${amount}`);
          } else {
            console.log(`  ✅ ${field} is valid`);
          }
        } else {
          console.log(`  ✅ ${field} is valid`);
        }
      });
    } else if (activeStep === 2) {
      const step2Fields = ['patientName', 'patientAge', 'medicalCondition', 'hospitalizationStatus', 'hospital', 'city'];
      
      step2Fields.forEach(field => {
        console.log(`  Checking ${field}:`, formData[field]);
        
        if (!formData[field] || formData[field].toString().trim() === '') {
          newErrors[field] = true;
          newFieldErrors[field] = 'This field is required';
          console.log(`  ❌ ${field} is required`);
        } else {
          console.log(`  ✅ ${field} is valid`);
        }
      });
    } else if (activeStep === 3) {
      console.log(`  Checking story:`, formData.story?.length);
      
      if (!formData.story || formData.story.trim() === '') {
        newErrors.story = true;
        newFieldErrors.story = 'Story is required';
        console.log(`  ❌ story is required`);
      } else if (formData.story.length < FIELD_LIMITS.story.min) {
        newErrors.story = true;
        newFieldErrors.story = `Story must be at least ${FIELD_LIMITS.story.min} characters`;
        console.log(`  ❌ story too short: ${formData.story.length} < ${FIELD_LIMITS.story.min}`);
      } else {
        console.log(`  ✅ story is valid`);
      }
      
      console.log(`  Checking agreeTerms:`, formData.agreeTerms);
      
      if (!formData.agreeTerms) {
        newErrors.agreeTerms = true;
        newFieldErrors.agreeTerms = 'You must agree to the terms and conditions';
        console.log(`  ❌ agreeTerms is required`);
      } else {
        console.log(`  ✅ agreeTerms is valid`);
      }
    }

    console.log('📊 Validation result:', {
      hasErrors: Object.keys(newErrors).length > 0,
      errors: newErrors,
      fieldErrors: newFieldErrors
    });

    setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Form submitted for step:', activeStep);

    // Validate the current step
    const isValid = validateStep();
    console.log('✅ Step validation result:', isValid ? 'Valid' : 'Invalid');

    if (!isValid) {
      console.log('❌ Validation failed, stopping submission');
      setApiError('Please fill all required fields correctly.');
      return;
    }

    try {
      setIsLoading(true);
      setApiError('');

      if (activeStep === 1) {
        console.log('📤 Saving step 1 data via API...');

        // Check OTP and otpTxnId
        if (!otp || otp.length !== 6) {
          throw new Error('OTP not verified. Please verify your mobile number first.');
        }
        
        if (!otpTxnId) {
          throw new Error('Missing OTP transaction ID. Please verify your mobile number first.');
        }

        const payload = {
          title: formData.title,
          goalAmount: formData.goalAmount,
          purpose: formData.purpose || "medical",
          otpTxnId: otpTxnId,
          otp: otp,
          patientRelation: formData.patientRelation,
          education: formData.education,
          employmentStatus: formData.employmentStatus,
          heardAbout: formData.heardAbout,
        };

        console.log('📤 Calling API with OTP verification...');

        const result = await fundraiserApi.saveFundraiserBasic(payload);

        if (result.success) {
          console.log('✅ Step 1 saved successfully, fundraiserId:', result.fundraiserId || result.id);
          setFundraiserId(result.fundraiserId || result.id);
          localStorage.setItem('fundraiserId', result.fundraiserId || result.id);
          setActiveStep(2);
          console.log('➡️ Moving to step 2');
        } else {
          throw new Error(result.message || 'Failed to save basic details');
        }

      } else if (activeStep === 2) {
        if (!fundraiserId) {
          throw new Error('Fundraiser ID not found. Please go back to step 1.');
        }

        console.log('📤 Saving step 2 data via API...');
        const result = await fundraiserApi.savePatientDetails(fundraiserId, formData);

        if (result.success) {
          console.log('✅ Step 2 saved successfully');

          console.log('🤖 Generating story via API...');
          try {
            const storyResult = await fundraiserApi.generateStory(fundraiserId);

            if (storyResult.success) {
              setGeneratedStory(storyResult);
              if (storyResult.raw) {
                console.log('📝 Setting generated story');
                handleInputChange('story', storyResult.raw);
              }
            } else {
              console.warn('Story generation failed:', storyResult.message);
              setGeneratedStory(null);
            }
          } catch (storyError) {
            console.warn('Story generation error:', storyError);
            setGeneratedStory(null);
          }

          setActiveStep(3);
          console.log('➡️ Moving to step 3');
        } else {
          throw new Error(result.message || 'Failed to save patient details');
        }

      } else if (activeStep === 3) {
        if (!fundraiserId) {
          throw new Error('Fundraiser ID not found. Please go back to step 1.');
        }

        console.log('📤 Submitting story via API...');

        const storyData = {
          title: formData.title,
          storyHtml: formData.story,
          add_documents: formData.addDocuments,
          consent_insurance: formData.consentInsurance,
          agree_terms: formData.agreeTerms
        };

        const storyResult = await fundraiserApi.submitStory(fundraiserId, storyData);

        if (storyResult.success) {
          console.log('✅ Story submitted successfully');

          localStorage.setItem('submittedFundraiserId', fundraiserId);
          localStorage.setItem('submissionTime', Date.now().toString());

          clearDraft();
          localStorage.removeItem('fundraiserId');

          setFormData({
            title: '',
            goalAmount: '',
            purpose: 'medical',
            patientRelation: '',
            education: '',
            employmentStatus: '',
            heardAbout: '',
            patientName: '',
            patientAge: '',
            medicalCondition: '',
            hospitalizationStatus: '',
            hospital: '',
            city: '',
            story: '',
            addDocuments: false,
            consentInsurance: true,
            agreeTerms: false,
          });

          setActiveStep(1);

          setShowSubmissionSuccess(true);
          console.log('✅ Success screen shown');
        } else {
          throw new Error(storyResult.message || 'Failed to submit story');
        }
      }

    } catch (error) {
      console.error('❌ Form submission error:', error);

      let errorMessage = error.message || 'Something went wrong. Please try again.';

      if (typeof error === 'object') {
        if (error.message && error.message.includes('goalAmount')) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      }

      // Handle OTP-specific errors
      if (errorMessage.toLowerCase().includes('otp') || 
          errorMessage.toLowerCase().includes('invalid') ||
          errorMessage.toLowerCase().includes('expired')) {
        errorMessage = 'OTP verification failed. Please verify your mobile number again.';
      }

      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
      console.log('⏳ Loading state set to false');
    }
  };

  const handleBack = () => {
    console.log('🔙 Going back from step:', activeStep);
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleUseGeneratedStory = async () => {
    if (!fundraiserId) {
      setApiError('Fundraiser ID not found. Please save basic details first.');
      return;
    }
    
    try {
      setIsLoading(true);
      console.log('🤖 Generating story via API...');
      const storyResult = await fundraiserApi.generateStory(fundraiserId);
      
      if (storyResult.success) {
        setGeneratedStory(storyResult);
        if (storyResult.raw) {
          console.log('📝 Using generated story');
          handleInputChange('story', storyResult.raw);
        }
      } else {
        throw new Error(storyResult.message || 'Failed to generate story');
      }
    } catch (error) {
      console.error('❌ Story generation error:', error);
      setApiError(error.message || 'Failed to generate story. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add this function to render the story generator button
  const renderStoryGenerator = () => {
    if (activeStep === 3 && fundraiserId) {
      return (
        <div className="mb-4">
          <button
            type="button"
            onClick={handleUseGeneratedStory}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition-colors disabled:opacity-50 text-sm"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </>
            ) : (
              '✨ Auto-generate Story'
            )}
          </button>
          <p className="text-xs text-gray-500 mt-1 text-left">
            Our AI will create a compelling story based on your fundraiser details.
          </p>
        </div>
      );
    }
    return null;
  };

  const getSubmitButtonText = () => {
    if (isLoading) {
      return isSubmitting ? 'Publishing...' : 'Saving...';
    }
    
    switch (activeStep) {
      case 1: 
        if (!otp || !otpTxnId) {
          return 'Verify Mobile First';
        }
        return 'Save & Continue →';
      case 2: return 'Save & Continue →';
      case 3: return 'Publish Fundraiser';
      default: return 'Continue';
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <div className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                Fundraiser Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                onBlur={() => handleBlur('title')}
                placeholder="Enter fundraiser title"
                className={`
                  w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                  focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                  placeholder:text-gray-400 text-gray-900 text-sm
                  ${fieldErrors.title 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                maxLength={FIELD_LIMITS.title.max}
                required
              />
              {fieldErrors.title && (
                <p className="mt-1 text-xs text-red-600 text-left">
                  {fieldErrors.title}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                Goal Amount (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="goalAmount"
                value={formData.goalAmount}
                onChange={(e) => handleInputChange('goalAmount', e.target.value)}
                onBlur={() => handleBlur('goalAmount')}
                placeholder="Minimum ₹2000"
                className={`
                  w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                  focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                  placeholder:text-gray-400 text-gray-900 text-sm
                  ${fieldErrors.goalAmount 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                required
              />
              <div className="mt-1 text-xs text-gray-500 text-left">
                Minimum amount: ₹2,000 | Maximum amount: ₹{FIELD_LIMITS.goalAmount.max.toLocaleString()}
              </div>
              {fieldErrors.goalAmount && (
                <p className="mt-1 text-xs text-red-600 text-left">
                  {fieldErrors.goalAmount}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                  Patient Relation <span className="text-red-500">*</span>
                </label>
                <select
                  name="patientRelation"
                  value={formData.patientRelation}
                  onChange={(e) => handleInputChange('patientRelation', e.target.value)}
                  className={`
                    w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                    focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                    text-gray-900 text-sm bg-white
                    ${fieldErrors.patientRelation 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  required
                >
                  <option value="">Select relation</option>
                  <option value="self">Self</option>
                  <option value="spouse">Spouse</option>
                  <option value="child">Child</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </select>
                {fieldErrors.patientRelation && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {fieldErrors.patientRelation}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                  Education <span className="text-red-500">*</span>
                </label>
                <select
                  name="education"
                  value={formData.education}
                  onChange={(e) => handleInputChange('education', e.target.value)}
                  className={`
                    w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                    focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                    text-gray-900 text-sm bg-white
                    ${fieldErrors.education 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  required
                >
                  <option value="">Select education</option>
                  <option value="high_school">High School</option>
                  <option value="graduate">Graduate</option>
                  <option value="post_graduate">Post Graduate</option>
                  <option value="doctorate">Doctorate</option>
                  <option value="other">Other</option>
                </select>
                {fieldErrors.education && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {fieldErrors.education}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                Employment Status <span className="text-red-500">*</span>
              </label>
              <select
                name="employmentStatus"
                value={formData.employmentStatus}
                onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
                className={`
                  w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                  focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                  text-gray-900 text-sm bg-white
                  ${fieldErrors.employmentStatus 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                required
              >
                <option value="">Select employment status</option>
                <option value="employed">Employed</option>
                <option value="self_employed">Self Employed</option>
                <option value="student">Student</option>
                <option value="unemployed">Unemployed</option>
                <option value="retired">Retired</option>
              </select>
              {fieldErrors.employmentStatus && (
                <p className="mt-1 text-xs text-red-600 text-left">
                  {fieldErrors.employmentStatus}
                </p>
              )}
            </div>

            {/* Add missing heardAbout field */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                How did you hear about us? <span className="text-red-500">*</span>
              </label>
              <select
                name="heardAbout"
                value={formData.heardAbout}
                onChange={(e) => handleInputChange('heardAbout', e.target.value)}
                className={`
                  w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                  focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                  text-gray-900 text-sm bg-white
                  ${fieldErrors.heardAbout 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                required
              >
                <option value="">Select option</option>
                <option value="friend">Friend/Family</option>
                <option value="social_media">Social Media</option>
                <option value="search_engine">Search Engine</option>
                <option value="news">News Article</option>
                <option value="other">Other</option>
              </select>
              {fieldErrors.heardAbout && (
                <p className="mt-1 text-xs text-red-600 text-left">
                  {fieldErrors.heardAbout}
                </p>
              )}
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                  Patient Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="patientName"
                  value={formData.patientName}
                  onChange={(e) => handleInputChange('patientName', e.target.value)}
                  onBlur={() => handleBlur('patientName')}
                  placeholder="Full name"
                  className={`
                    w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                    focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                    placeholder:text-gray-400 text-gray-900 text-sm
                    ${fieldErrors.patientName 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  maxLength={FIELD_LIMITS.patientName.max}
                  required
                />
                {fieldErrors.patientName && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {fieldErrors.patientName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                  Age <span className="text-red-500">*</span>
                </label>
                <select
                  name="patientAge"
                  value={formData.patientAge}
                  onChange={(e) => handleInputChange('patientAge', e.target.value)}
                  className={`
                    w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                    focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                    text-gray-900 text-sm bg-white
                    ${fieldErrors.patientAge 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  required
                >
                  <option value="">Select age</option>
                  {Array.from({ length: 100 }, (_, i) => (
                    <option key={i + 1} value={(i + 1).toString()}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                {fieldErrors.patientAge && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {fieldErrors.patientAge}
                  </p>
                )}
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                Medical Condition <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="medicalCondition"
                  value={formData.medicalCondition}
                  onChange={(e) => handleInputChange('medicalCondition', e.target.value)}
                  onFocus={() => {
                    if (formData.medicalCondition.trim().length >= 2) {
                      filterMedicalConditions(formData.medicalCondition);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowMedicalConditionDropdown(false), 200)}
                  placeholder="Type to search medical condition"
                  className={`
                    w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                    focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                    placeholder:text-gray-400 text-gray-900 text-sm bg-white
                    ${fieldErrors.medicalCondition 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  maxLength={FIELD_LIMITS.medicalCondition.max}
                  required
                />
                
                {/* Custom dropdown arrow */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {showMedicalConditionDropdown && filteredConditions.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredConditions.map((condition, index) => (
                      <div
                        key={index}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectMedicalCondition(condition);
                        }}
                        className="px-3.5 py-2.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-b-0 hover:text-gray-900"
                      >
                        {condition}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {fieldErrors.medicalCondition && (
                <p className="mt-1 text-xs text-red-600 text-left">
                  {fieldErrors.medicalCondition}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                  Hospitalization Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="hospitalizationStatus"
                  value={formData.hospitalizationStatus}
                  onChange={(e) => handleInputChange('hospitalizationStatus', e.target.value)}
                  className={`
                    w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                    focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                    text-gray-900 text-sm bg-white
                    ${fieldErrors.hospitalizationStatus 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  required
                >
                  <option value="">Select status</option>
                  <option value="admitted">Currently Admitted</option>
                  <option value="discharged">Discharged</option>
                  <option value="regular_visits">Regular Visits</option>
                  <option value="planned">Planned Admission</option>
                </select>
                {fieldErrors.hospitalizationStatus && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {fieldErrors.hospitalizationStatus}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  onBlur={() => handleBlur('city')}
                  placeholder="City"
                  className={`
                    w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                    focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                    placeholder:text-gray-400 text-gray-900 text-sm
                    ${fieldErrors.city 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  maxLength={FIELD_LIMITS.city.max}
                  required
                />
                {fieldErrors.city && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {fieldErrors.city}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                Hospital Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="hospital"
                value={formData.hospital}
                onChange={(e) => handleInputChange('hospital', e.target.value)}
                onBlur={() => handleBlur('hospital')}
                placeholder="Hospital name"
                className={`
                  w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                  focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                  placeholder:text-gray-400 text-gray-900 text-sm
                  ${fieldErrors.hospital 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                maxLength={FIELD_LIMITS.hospital.max}
                required
              />
              {fieldErrors.hospital && (
                <p className="mt-1 text-xs text-red-600 text-left">
                  {fieldErrors.hospital}
                </p>
              )}
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-4 animate-fade-in">
            {renderStoryGenerator()}
            
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                Your Story <span className="text-red-500">*</span>
              </label>
              <textarea
                name="story"
                value={formData.story}
                onChange={(e) => handleInputChange('story', e.target.value)}
                onBlur={() => handleBlur('story')}
                placeholder="Tell your story... Share details about the situation, why you need help, and how donations will be used."
                className={`
                  w-full px-3.5 py-2.5 rounded-lg border-2 transition-all duration-300
                  focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                  placeholder:text-gray-400 text-gray-900 text-sm
                  ${fieldErrors.story 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                rows={8}
                maxLength={FIELD_LIMITS.story.max}
                required
              />
              {fieldErrors.story && (
                <p className="mt-1 text-xs text-red-600 text-left">
                  {fieldErrors.story}
                </p>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={(e) => handleInputChange('agreeTerms', e.target.checked)}
                  className="mt-1 h-5 w-5 text-gsg-teal rounded border-gray-300 focus:ring-gsg-teal focus:ring-offset-0"
                  required
                />
                <label htmlFor="agreeTerms" className="text-sm text-gray-700 text-left">
                  I agree to all <a href="#" className="text-gsg-teal hover:underline font-medium">terms and conditions</a>, including that funds raised will be used solely for the stated purpose.
                </label>
                {fieldErrors.agreeTerms && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {fieldErrors.agreeTerms}
                  </p>
                )}
              </div>
              
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="consentInsurance"
                  checked={formData.consentInsurance}
                  onChange={(e) => handleInputChange('consentInsurance', e.target.checked)}
                  className="mt-1 h-5 w-5 text-gsg-teal rounded border-gray-300 focus:ring-gsg-teal focus:ring-offset-0"
                />
                <label htmlFor="consentInsurance" className="text-sm text-gray-700 text-left">
                  I consent to receiving insurance and financial service offers from trusted partners (optional)
                </label>
              </div>
              
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="addDocuments"
                  checked={formData.addDocuments}
                  onChange={(e) => handleInputChange('addDocuments', e.target.checked)}
                  className="mt-1 h-5 w-5 text-gsg-teal rounded border-gray-300 focus:ring-gsg-teal focus:ring-offset-0"
                />
                <label htmlFor="addDocuments" className="text-sm text-gray-700 text-left">
                  I will add medical documents to increase trust (recommended)
                </label>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gsg-teal/5">
      {(isLoading || isSubmitting) && (
        <LoadingOverlay 
          message={
            isSubmitting 
              ? "Publishing your fundraiser..." 
              : activeStep === 3 
                ? "Saving your story..." 
                : "Saving your fundraiser..."
          } 
        />
      )}
      
      <AutoSaveIndicator 
        lastSaved={lastSaved} 
        isSaving={isSaving} 
        saveError={saveError} 
      />
      
      <div className="relative min-h-screen flex">
        {/* Left Section - Same as VerifyMobileNumber */}
        <div className="hidden lg:flex lg:w-1/3 relative bg-gradient-to-br from-gsg-teal/5 via-white to-white">
          <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-12">
            <div className="max-w-lg">
              {/* Logo & Title */}
              <div className={`mb-12 transition-all duration-1000`}>
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gsg-teal to-gsg-teal-dark rounded-2xl mb-8 shadow-2xl shadow-gsg-teal/20">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                
                <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Complete Your <span className="text-gsg-teal">Fundraiser</span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Share your story with compassionate donors worldwide.
                </p>
              </div>
              
              {/* Features */}
              <div className={`transition-all duration-1000 delay-300`}>
                <div className="space-y-5">
                  {features.map((feature, index) => (
                    <div 
                      key={index}
                      className={`p-5 bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-all duration-500 
                        ${currentFeature === index ? 'border-gsg-teal/30 shadow-xl shadow-gsg-teal/10 scale-[1.02]' : 
                          'border-gray-100 shadow-sm hover:border-gray-200'
                        }
                      `}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <span className="text-2xl">{feature.icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-lg mb-1.5 text-left">{feature.title}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed text-left">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center Content - Matches VerifyMobileNumber */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center py-6 px-4 lg:px-8">
          <div className="w-full max-w-3xl mx-auto">
            {/* Centered Heading Section */}
            <div className="text-center mb-10 w-full">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Complete Your Fundraiser</h2>
              <p className="text-gray-600 mt-3 text-lg">Share your story with compassionate donors worldwide</p>
            </div>
            
            {/* Progress Indicator - Same style as VerifyMobileNumber */}
            <div className="flex items-center justify-center space-x-12 mb-10 w-full">
              {[1, 2, 3].map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center text-base font-bold
                    ${index + 1 === activeStep ? 'bg-gsg-teal text-white shadow-lg' : 
                      index + 1 < activeStep ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'}
                  `}>
                    {index + 1}
                  </div>
                  <div className="text-sm font-semibold text-gray-700 mt-3">
                    {index === 0 ? 'Details' : index === 1 ? 'Patient' : 'Story'}
                  </div>
                  <div className="h-1 w-full bg-gray-200 mt-3 rounded-full">
                    <div className={`h-1 w-full ${index + 1 < activeStep ? 'bg-green-500' : index + 1 === activeStep ? 'bg-gsg-teal' : 'bg-gray-200'} rounded-full`}></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Form Container - Same width as VerifyMobileNumber */}
            <div className="w-full flex justify-center">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl">
                {/* Header - Same compact style */}
                <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gsg-teal/5 to-white">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-gsg-teal to-gsg-teal-dark flex items-center justify-center shadow-sm`}>
                      <span className="text-lg text-white">
                        📄
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {activeStep === 1 ? 'Fundraiser Details' : activeStep === 2 ? 'Patient Information' : 'Tell Your Story'}
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Step {activeStep} of 3: {activeStep === 1 ? 'Basic information' : activeStep === 2 ? 'Patient details' : 'Share your story'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Content - Same compact style */}
                <div className="px-6 py-5">
                  {apiError && typeof apiError === 'object' ? (
                    <ErrorMessage message={apiError.message} type={apiError.type} />
                  ) : apiError ? (
                    <ErrorMessage message={apiError} type="error" />
                  ) : null}

                  {/* Add OTP warning if needed */}
                  {activeStep === 1 && (!otp || !otpTxnId) && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.342 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <div>
                          <p className="text-xs text-yellow-700 font-medium text-left">
                            OTP verification required
                          </p>
                          <p className="text-xs text-yellow-600 mt-1 text-left">
                            Please verify your mobile number before creating a fundraiser.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {renderStepContent()}

                    {/* Submit Button - Same style as VerifyMobileNumber */}
                    <div className={`flex ${activeStep > 1 ? 'justify-between' : 'justify-end'} mt-6 pt-5 border-t border-gray-100`}>
                      {activeStep > 1 && (
                        <button
                          type="button"
                          onClick={handleBack}
                          disabled={isLoading || isSubmitting}
                          className="px-6 py-2.5 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all disabled:opacity-50"
                        >
                          ← Back
                        </button>
                      )}
                      
                      <button
                        type="submit"
                        disabled={isLoading || isSubmitting || (activeStep === 1 && (!otp || !otpTxnId))}
                        className={`
                          px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
                          ${isLoading || isSubmitting || (activeStep === 1 && (!otp || !otpTxnId))
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gsg-teal to-gsg-teal-dark text-white hover:shadow-md hover:shadow-gsg-teal/25'
                          }
                          flex items-center justify-center
                        `}
                      >
                        {(isLoading || isSubmitting) ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {getSubmitButtonText()}
                          </>
                        ) : (
                          getSubmitButtonText()
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Footer - Same style */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-blue-700 text-left">
                        Your information is secure and will only be used for fundraising purposes.
                      </p>
                    </div>
                  </div>

                  {/* Fundraiser ID Display */}
                  {fundraiserId && (
                    <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xs text-emerald-700 font-medium text-left">
                          Fundraiser ID: {fundraiserId}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer - Same style */}
            <div className="text-center mt-8 w-full">
              <p className="text-xs text-gray-500">
                By continuing, you agree to our Terms & Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Show new success screen */}
      {showSubmissionSuccess && (
        <FundraiserSubmissionSuccess 
          fundraiserId={fundraiserId}
          onClose={() => {
            setShowSubmissionSuccess(false);
            // Clear submission data when closing
            localStorage.removeItem('submittedFundraiserId');
            localStorage.removeItem('submissionTime');
            navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
};

export default DetailsScreen_1;